import { motion } from 'framer-motion';
import { Clock, Phone, Mail, RefreshCw } from 'lucide-react';
import { formatValidUntil } from '../../utils';

interface ExpiredProposalProps {
  validUntil: string;
}

export function ExpiredProposal({ validUntil }: ExpiredProposalProps) {
  return (
    <div className="min-h-screen bg-surface-dark flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(rgba(237, 44, 44, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(237, 44, 44, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
        {/* Hazard Stripes */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 20px,
              rgba(237, 44, 44, 0.5) 20px,
              rgba(237, 44, 44, 0.5) 40px
            )`,
          }}
        />
      </div>

      <motion.div
        className="relative w-full max-w-lg"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Warning Header */}
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-8 py-8 text-center relative">
            {/* Icon */}
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4"
              animate={{ rotate: [0, -5, 5, -5, 0] }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <Clock className="w-10 h-10 text-white" />
            </motion.div>

            <h1 className="font-display text-3xl text-white tracking-wide mb-2">
              PROPOSAL EXPIRED
            </h1>
            <p className="text-white/90">
              This proposal is no longer valid
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Expiry Date */}
            <div className="text-center mb-8">
              <p className="text-gray-500 mb-2">This proposal expired on</p>
              <p className="font-heading text-2xl text-hul-navy">
                {formatValidUntil(validUntil)}
              </p>
            </div>

            {/* Divider */}
            <div className="relative h-px bg-gray-200 my-8">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4">
                <span className="text-gray-400 text-sm">What to do next</span>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-4">
              {/* Request New Proposal */}
              <motion.a
                href="#"
                className="flex items-center gap-4 p-4 bg-hul-navy/5 rounded-xl hover:bg-hul-navy/10 transition-colors group"
                whileHover={{ x: 4 }}
              >
                <div className="w-12 h-12 bg-hul-red rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <RefreshCw className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-heading text-hul-navy">Request Updated Proposal</p>
                  <p className="text-sm text-gray-500">Get a new quote with current pricing</p>
                </div>
              </motion.a>

              {/* Call */}
              <motion.a
                href="tel:1-800-555-LIFT"
                className="flex items-center gap-4 p-4 bg-hul-navy/5 rounded-xl hover:bg-hul-navy/10 transition-colors group"
                whileHover={{ x: 4 }}
              >
                <div className="w-12 h-12 bg-hul-orange rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-heading text-hul-navy">Call Us Directly</p>
                  <p className="text-sm text-gray-500">1-800-555-LIFT</p>
                </div>
              </motion.a>

              {/* Email */}
              <motion.a
                href="mailto:sales@herculift.com"
                className="flex items-center gap-4 p-4 bg-hul-navy/5 rounded-xl hover:bg-hul-navy/10 transition-colors group"
                whileHover={{ x: 4 }}
              >
                <div className="w-12 h-12 bg-hul-navy rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-heading text-hul-navy">Email Our Team</p>
                  <p className="text-sm text-gray-500">sales@herculift.com</p>
                </div>
              </motion.a>
            </div>

            {/* Note */}
            <p className="mt-8 text-center text-sm text-gray-500">
              Pricing and availability may have changed since this proposal was created.
              <br />
              Contact us for the most current information.
            </p>
          </div>
        </div>

        {/* Company Name */}
        <div className="mt-6 text-center">
          <span className="text-white/40 text-sm font-mono">
            Herc-U-Lift Equipment Solutions
          </span>
        </div>
      </motion.div>
    </div>
  );
}
